import {
  Outlet,
  createFileRoute,
  Link,
  useParams,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  BookOpen,
  Building2,
  Layers,
  FileText,
  FileCode,
  Globe,
  LayoutGrid,
  Package,
  type LucideIcon,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  listKnowledgeSourcesServer,
  type KnowledgeSource,
  type SourceListResponse,
} from "@/server/knowledge-sources";

type KnowledgeTopTab = "companyProfiles" | "products" | "knowledges";

interface KnowledgeTabItem {
  key: KnowledgeTopTab;
  label: string;
  icon: LucideIcon;
  to:
    | "/workspace/$workspaceSlug/knowledge/company-profiles"
    | "/workspace/$workspaceSlug/knowledge/products"
    | "/workspace/$workspaceSlug/knowledge/sources";
}

export const Route = createFileRoute("/workspace/$workspaceSlug/knowledge")({
  loader: async ({ parentMatchPromise }) => {
    const parent = await parentMatchPromise;
    const workspaceId = parent?.loaderData?.workspaceId;

    if (!workspaceId || typeof workspaceId !== "string") {
      throw redirect({ to: "/onboarding/profile" });
    }

    return { workspaceId };
  },
  component: KnowledgeLayout,
});

function KnowledgeLayout() {
  const { t } = useTranslation();
  const { workspaceSlug } = useParams({ from: Route.id });
  const { workspaceId } = Route.useLoaderData();
  const location = useLocation();
  const activeTopTab = getActiveTopTab(location.pathname);

  const { data } = useQuery<SourceListResponse>({
    queryKey: ["knowledge-sources", workspaceId, "knowledge-sidebar"],
    queryFn: async () => {
      const result = await listKnowledgeSourcesServer({
        data: {
          workspaceId,
          page: 1,
          pageSize: 100, // Backend max is 100
        },
      });
      return result as SourceListResponse;
    },
  });

  const contentSources = (data?.items ?? [])
    .filter(
      (source: KnowledgeSource) =>
        source.source_type === "document" || source.source_type === "webpage",
    )
    .slice()
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  const topTabs: KnowledgeTabItem[] = [
    {
      key: "companyProfiles",
      label: t("pages:knowledge.tabs.companyProfiles"),
      icon: Building2,
      to: "/workspace/$workspaceSlug/knowledge/company-profiles",
    },
    {
      key: "products",
      label: t("pages:knowledge.tabs.products"),
      icon: Package,
      to: "/workspace/$workspaceSlug/knowledge/products",
    },
    {
      key: "knowledges",
      label: t("pages:knowledge.tabs.knowledges"),
      icon: BookOpen,
      to: "/workspace/$workspaceSlug/knowledge/sources",
    },
  ];

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Page Header */}
      <div className="h-[64px] px-6 shrink-0">
        <div className="flex h-full items-center">
          <h1 className="text-[16px] font-semibold text-gray-900">{t("pages:knowledge.title")}</h1>
        </div>
      </div>
      <Separator className="bg-[#dfdfdf] shrink-0" />

      <div className="flex h-[58px] items-end gap-8 border-b border-gray-200 px-6">
        {topTabs.map((tab) => (
          <Link
            key={tab.key}
            to={tab.to}
            params={{ workspaceSlug }}
            className={cn(
              "flex h-full items-center gap-2 border-b px-0 text-[14px] font-normal transition-colors",
              activeTopTab === tab.key
                ? "border-gray-950 text-gray-950"
                : "border-transparent text-gray-500 hover:text-gray-900",
            )}
          >
            <tab.icon className="size-4 shrink-0" />
            {tab.label}
          </Link>
        ))}
      </div>

      {/* Sidebar + Content */}
      <div className="flex flex-1 min-h-0">
        {activeTopTab === "knowledges" ? (
          <KnowledgeSecondarySidebar
            workspaceSlug={workspaceSlug}
            contentSources={contentSources}
            translate={t}
          />
        ) : null}
        {/* Main Content */}
        <div className="flex-1 overflow-auto bg-white">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function getActiveTopTab(pathname: string): KnowledgeTopTab {
  if (pathname.includes("/knowledge/products")) return "products";
  if (pathname.includes("/knowledge/company-profiles")) return "companyProfiles";
  return "knowledges";
}

interface KnowledgeSecondarySidebarProps {
  workspaceSlug: string;
  contentSources: KnowledgeSource[];
  translate: (key: string) => string;
}

function KnowledgeSecondarySidebar({
  workspaceSlug,
  contentSources,
  translate,
}: KnowledgeSecondarySidebarProps) {
  return (
    <div className="w-[200px] shrink-0 border-r border-gray-200 bg-white px-3 py-6">
      <div className="space-y-2">
        <div>
          <div className="space-y-1">
            <Link
              to="/workspace/$workspaceSlug/knowledge/sources"
              params={{ workspaceSlug }}
              activeProps={{ className: "bg-gray-100 text-gray-900" }}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <LayoutGrid className="h-4 w-4" />
              {translate("pages:knowledge.sidebar.sources")}
            </Link>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2 px-2 text-sm font-medium text-gray-500">
            <Layers className="h-4 w-4" />
            {translate("pages:knowledge.sidebar.contents")}
          </div>
          <div className="space-y-1 px-2">
            <Link
              to="/workspace/$workspaceSlug/knowledge/snippets"
              params={{ workspaceSlug }}
              activeProps={{ className: "bg-gray-100 text-gray-900" }}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-normal text-gray-700 hover:bg-gray-100"
            >
              <FileCode className="h-4 w-4 shrink-0" />
              {translate("pages:knowledge.sidebar.snippets")}
            </Link>
            {contentSources.map((source) => {
              const Icon = source.source_type === "webpage" ? Globe : FileText;

              if (source.source_type === "document") {
                return (
                  <Link
                    key={source.id}
                    to="/workspace/$workspaceSlug/knowledge/documents"
                    params={{ workspaceSlug }}
                    search={{
                      sourceId: source.id,
                      sourceName: source.name,
                    }}
                    activeProps={{ className: "bg-gray-100 text-gray-900" }}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-normal text-gray-700 hover:bg-gray-100"
                    title={source.name}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{source.name}</span>
                  </Link>
                );
              }

              return (
                <Link
                  key={source.id}
                  to="/workspace/$workspaceSlug/knowledge/websites"
                  params={{ workspaceSlug }}
                  search={{
                    sourceId: source.id,
                    sourceName: source.name,
                  }}
                  activeProps={{ className: "bg-gray-100 text-gray-900" }}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-normal text-gray-700 hover:bg-gray-100"
                  title={source.name}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{source.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
